import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, DateiMitVerknuepfungen, Ordner, VorgangsBezug } from "@kundeninfo/api-client";
import { Modal, ModalFuss, darfAbbrechen, useBeforeUnload } from "@kundeninfo/ui";
import { Hinweis, Optionen } from "@kundeninfo/ui/me-design";
import { Suchfeld } from "../meHuellen.js";
import { apiMeldung } from "../vorgangsdetail.js";
import { FolderTreeSelect } from "./FolderTreeSelect.js";
import { DOCUMENT_FILE_TEXT as T } from "./texts.js";
import "./documentFiles.css";

export type LinkFilesClient = Pick<ApiClient, "listFolders" | "listFiles" | "linkDocumentFile">;

/**
 * Das seitliche Panel „Vorhandene Datei verknüpfen“ (Spec „Abschnitt ‚Dateien‘ › Werkzeugleiste“): Ordnerbaum, Suche und
 * Mehrfachwahl über die Dateien des Kunden; schon verknüpfte (und Beleg-PDFs des Vorgangs) stehen ausgegraut da, ebenso
 * die nur lesbaren (`readOnly`, Belegdateien der Lieferantenvorgänge ohne Lieferantensicht, Entscheid 518).
 * „Verknüpfen“ ist die Bestätigung und verknüpft jede gewählte Datei (idempotent, die Datei bleibt in ihrem Ordner).
 * Die Wahl überlebt Ordnerwechsel und Suche — gewählt ist, was angekreuzt wurde, nicht nur, was gerade zu sehen ist.
 */
export function LinkFilesPanel(p: {
  client: LinkFilesClient; kundeId: string; bezug: VorgangsBezug;
  /** Kennungen, die der Vorgang schon führt. */
  linkedIds: ReadonlySet<string>;
  onChanged: () => Promise<void>; onClose: () => void;
}): ReactElement {
  const { client, kundeId } = p;
  const [folders, setFolders] = useState<Ordner[]>([]);
  const [files, setFiles] = useState<DateiMitVerknuepfungen[] | null>(null);
  const [folderFilter, setFolderFilter] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  /* Der Wächter gegen das Schließen des BROWSERS (A-K8): eine Wahl oder ein laufendes Verknüpfen. */
  useBeforeUnload(selected.length > 0 || running);

  useEffect(() => {
    let gone = false;
    Promise.all([client.listFolders(kundeId), client.listFiles(kundeId)])
      .then(([f, d]) => { if (!gone) { setFolders(f.items); setFiles(d.items); } })
      .catch((e: unknown) => { if (!gone) setMessage(apiMeldung(e, T.loadFailed)); });
    return () => { gone = true; };
  }, [client, kundeId]);

  const pattern = search.trim().toLocaleLowerCase("de");
  const visible = (files ?? []).filter((d) => (folderFilter === null || d.folderId === folderFilter)
    && d.originalName.toLocaleLowerCase("de").includes(pattern));
  const visibleIds = new Set(visible.map((d) => d.id));

  async function link(): Promise<void> {
    if (selected.length === 0 || running) return;
    setRunning(true);
    setMessage(null);
    const pending = [...selected];
    try {
      while (pending.length > 0) {
        await client.linkDocumentFile(kundeId, p.bezug, pending[0]!);
        pending.shift();
      }
    } catch (e) {
      setMessage(apiMeldung(e, T.linkFailed));
    }
    await p.onChanged();
    setRunning(false);
    // Gescheitert: Das Panel bleibt mit den noch nicht verknüpften Dateien stehen.
    if (pending.length === 0) p.onClose();
    else setSelected(pending);
  }

  async function cancel(): Promise<void> {
    if (running || !(await darfAbbrechen(selected.length > 0))) return;
    p.onClose();
  }

  return (
    <Modal titel={T.linkExisting} breite="mittel" onAbbrechen={() => { void cancel(); }} abbrechenGesperrt={running}>
      {message !== null && <Hinweis art="fehler">{message}</Hinweis>}
      <div className="doc-files__link">
        <FolderTreeSelect folders={folders} selected={folderFilter} rootLabel={T.rootFolder} onSelect={setFolderFilter} />
        <div className="doc-files__link-list">
          <Suchfeld name={T.search} wert={search} onText={setSearch} />
          {files !== null && visible.length === 0 && <p className="doc-files__muted">{T.noMatch}</p>}
          {visible.length > 0 && (
            <Optionen beschriftung={T.selection} mehrfach gesperrt={running}
              wert={selected.filter((id) => visibleIds.has(id))}
              onGeaendert={(d) => setSelected([...selected.filter((id) => !visibleIds.has(id)), ...(d.werte ?? [])])}>
              {visible.map((d) => (p.linkedIds.has(d.id)
                ? <option key={d.id} value={d.id} disabled>{T.alreadyLinked(d.originalName)}</option>
                : d.readOnly
                  ? <option key={d.id} value={d.id} disabled>{T.readOnlyFile(d.originalName)}</option>
                  : <option key={d.id} value={d.id}>{d.originalName}</option>))}
            </Optionen>
          )}
        </div>
      </div>
      <ModalFuss speichernText={T.linkConfirm} speichernGesperrt={selected.length === 0 || running}
        abbrechenGesperrt={running} onSpeichern={() => { void link(); }} onAbbrechen={() => { void cancel(); }} />
    </Modal>
  );
}
